import React from 'react';
import { useTabContext } from '@/components/context/TabContext';

/**
 * String Radio Group Component
 * Specialised for string values with a custom options array
 *
 * Uses TabContext to determine styling (adviser vs. customer)
 */
export const FormRadioGroup = ({
  name,
  label,
  options,
  value,
  onChange,
  error,
  required = false,
  showErrorLabel = false,
  isVertical = true,
}) => {
  const { selectedTab } = useTabContext();
  const isAdviser = selectedTab === 'adviser';
  const errorId = `${name}-error`;

  const getRadioClasses = (hasError) => {
    const baseClasses = 'mr-2 h-4 w-4 rounded-full appearance-none border-2 transition duration-200';
    const errorClasses = 'border-[#D32F2F]';
    const normalClasses = 'border-gray-300';

    // Dynamic colours and focus styles based on tab context
    const checkedClasses = isAdviser
      ? 'checked:bg-[#338BC5] checked:border-[#338BC5]'
      : 'checked:bg-[#8D1558] checked:border-[#8D1558]';

    const focusClasses = isAdviser
      ? 'focus:ring-2 focus:ring-offset-2 focus:ring-[#006EB7] focus:outline-none'
      : 'focus:ring-2 focus:ring-offset-2 focus:ring-[#8D1558] focus:outline-none';

    return `${baseClasses} ${hasError ? errorClasses : normalClasses} ${checkedClasses} ${focusClasses}`;
  };

  return (
    <div className="mb-6">
      <label className="block text-base font-medium text-[#0C0D1A] mb-2">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div
        className={`mt-1 ${isVertical ? 'space-y-2' : 'flex gap-6'}`}
        role="radiogroup"
        aria-required={required}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error && showErrorLabel ? errorId : undefined}
      >
        {options.map((option, index) => (
          <div key={index} className="flex items-center">
            <input
              type="radio"
              id={`${name}-${index}`}
              name={name}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
              className={getRadioClasses(error)}
            />
            <label
              htmlFor={`${name}-${index}`}
              className="text-base text-[#272938] font-475"
            >
              {option}
            </label>
          </div>
        ))}
      </div>
      {error && showErrorLabel && (
        <p id={errorId} className="mt-1 text-sm text-[#D32F2F]" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
